import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import NotFoundError from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/NotFoundError';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import usePortfolioRetireMutation from 'src/mutations/PortfolioRetireMutation';
import FlashesStore from 'src/stores/FlashesStore';

import Form from './Form';

const AdminPortfolioRetireFragment = graphql`
  fragment AdminPortfolioRetire_portfolio on Portfolio @throwOnFieldError {
    uuid
    title
  }
`;

/**
 * Admin form for retiring a portfolio once the user types the confirmation
 * word. Field errors, and a resolved query without a portfolio, throw to the
 * route's PlaceholderErrorBoundary.
 * @param {object} props - Component props.
 * @param {object|null} props.portfolio - Relay fragment reference for the portfolio.
 * @param {object} props.router - Found router, used to return to the portfolio after retiring.
 * @returns {React.ReactComponentElement} AdminPortfolioRetire component.
 */
function AdminPortfolioRetire({ portfolio: portfolioRef = null, router }) {
  const portfolio = useFragment(AdminPortfolioRetireFragment, portfolioRef);
  const [processing, setProcessing] = useState(false);
  const intl = useIntl();
  const retirePortfolio = usePortfolioRetireMutation();

  if (!portfolio) {
    // A completed query without a portfolio would otherwise never leave the spinner.
    throw new NotFoundError('AdminPortfolioRetire: the portfolio query returned no portfolio');
  }

  const { title, uuid } = portfolio;
  const pageTitle = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_retire.page.title', defaultMessage: 'Enosi Admin - Retire portfolio: {portfolioTitle}' }, { portfolioTitle: title });
  const breadcrumbAdmin = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_retire.breadcrumbs.admin.label', defaultMessage: 'Admin' });
  const breadcrumbPortfolios = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_retire.breadcrumbs.portfolios.label', defaultMessage: 'Portfolios' });
  const breadcrumbRetirePortfolio = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_retire.breadcrumbs.retire_portfolio.label', defaultMessage: 'Retire portfolio' });
  const mutationName = 'retirePortfolio';

  const formSuccessHandler = (response, errors) => {
    setProcessing(false);

    if (errors && errors.length > 0) {
      errors.forEach((error) => FlashesStore.flash(FlashesStore.ERROR, {
        message: FlashesStore.ERROR,
        source: {
          errors: [error],
        },
      }, mutationName));
      return;
    }

    const { uuid: retiredPortfolioId } = response.retirePortfolio;

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      <span>
        <FormattedMessage
          id="admin.admin_portfolio.admin_portfolio_retire.form.response_message.success"
          defaultMessage="Successfully retired <strong>{title}</strong> {uuid}."
          values={{
            strong: (chunks) => <strong>{chunks}</strong>,
            title,
            uuid,
          }}
        />
      </span>,
      mutationName,
    );
    router.push(`/admin/portfolios/${retiredPortfolioId}`);
  };

  const formFailureHandler = (error) => {
    setProcessing(false);
    FlashesStore.flash(FlashesStore.ERROR, error, mutationName);
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    setProcessing(true);
    retirePortfolio(
      uuid,
      formSuccessHandler,
      formFailureHandler,
    );
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPortfolios, path: '/admin/portfolios' },
          { name: title, path: `/admin/portfolios/${uuid}` },
          { name: breadcrumbRetirePortfolio },
        ]}
      />
      <Form
        handleSubmit={handleSubmit}
        processing={processing}
        portfolioTitle={title}
        portfolioUuid={uuid}
      />
    </>
  );
}

export default AdminPortfolioRetire;

AdminPortfolioRetire.propTypes = {
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  portfolio: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};
